import React, { useRef, useEffect, useState, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import {
  FaTrash,
  FaCopy,
  FaThumbtack,
  FaPalette,
  FaPlay,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";
import { useMultiSelect, SelectableItemType } from "@/hooks/use-multi-select";
import useTranslation from "@/hooks/use-translation";
import { ContextMenuItem, ContextMenuDivider } from "./context-menu-helpers";
import ColorPicker from "./color-picker";
import { MotionDiv } from "./motion-types";
import { Z_INDEX } from "@/utils/z-index";

interface MultiSelectContextMenuProps {
  /** Position of the context menu */
  position: { x: number; y: number };
  /** Handler to close the menu */
  onClose: () => void;

  /** Handler for deleting selected dashboards */
  onDeleteDashboards?: (ids: string[]) => Promise<void>;
  /** Handler for duplicating selected dashboards */
  onDuplicateDashboards?: (ids: string[]) => Promise<void>;
  /** Handler for pinning selected dashboards */
  onPinDashboards?: (ids: string[], pinned: boolean) => Promise<void>;
  /** Handler for unpinning selected dashboards */
  onUnpinDashboards?: (ids: string[]) => Promise<void>;
  /** Handler for changing color of selected dashboards */
  onChangeDashboardsColor?: (ids: string[], color: string) => Promise<void>;

  /** Handler for deleting selected trackers */
  onDeleteTrackers?: (ids: string[]) => Promise<void>;
  /** Handler for activating selected trackers */
  onActivateTrackers?: (ids: string[]) => Promise<void>;
  /** Handler for deactivating selected trackers */
  onDeactivateTrackers?: (ids: string[]) => Promise<void>;
  /** Handler for running selected trackers */
  onRunTrackers?: (ids: string[]) => Promise<void>;

  /** Handler for deleting selected enrichments */
  onDeleteEnrichments?: (ids: string[]) => Promise<void>;
  /** Handler for activating selected enrichments */
  onActivateEnrichments?: (ids: string[]) => Promise<void>;
  /** Handler for deactivating selected enrichments */
  onDeactivateEnrichments?: (ids: string[]) => Promise<void>;
  /** Handler for running selected enrichments */
  onRunEnrichments?: (ids: string[]) => Promise<void>;

  /** Handler for deleting selected alerts */
  onDeleteAlerts?: (ids: string[]) => Promise<void>;
  /** Handler for activating selected alerts */
  onActivateAlerts?: (ids: string[]) => Promise<void>;
  /** Handler for deactivating selected alerts */
  onDeactivateAlerts?: (ids: string[]) => Promise<void>;
  /** Handler for running selected alerts */
  onRunAlerts?: (ids: string[]) => Promise<void>;

  /** Handler for deleting selected widgets */
  onDeleteWidgets?: (ids: string[]) => Promise<void>;

  /** Handler for deleting selected investigations */
  onDeleteInvestigations?: (ids: string[]) => Promise<void>;
}

/**
 * Context menu for multi-selected items.
 * Displays when right-clicking on a selected item,
 * showing bulk actions based on the selected item type.
 */
const MultiSelectContextMenu: React.FC<MultiSelectContextMenuProps> = ({
  position,
  onClose,
  onDeleteDashboards,
  onDuplicateDashboards,
  onPinDashboards,
  onUnpinDashboards,
  onChangeDashboardsColor,
  onDeleteTrackers,
  onActivateTrackers,
  onDeactivateTrackers,
  onRunTrackers,
  onDeleteEnrichments,
  onActivateEnrichments,
  onDeactivateEnrichments,
  onRunEnrichments,
  onDeleteAlerts,
  onActivateAlerts,
  onDeactivateAlerts,
  onRunAlerts,
  onDeleteWidgets,
  onDeleteInvestigations,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const { selectedIds, itemType, selectionCount, clearSelection } = useMultiSelect();
  const { t } = useTranslation();

  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Adjust position to stay within viewport
  const adjustedPosition = {
    x: Math.min(position.x, window.innerWidth - 240),
    y: Math.min(position.y, window.innerHeight - 350),
  };

  /**
   * Wrap an action handler with loading state, selection clearing, and menu closing
   */
  const wrapAction = useCallback(
    (handler?: (ids: string[]) => Promise<void>) => async () => {
      if (!handler || selectedIds.length === 0) return;

      setIsLoading(true);
      try {
        await handler(selectedIds);
        clearSelection();
        onClose();
      } catch (error) {
        console.error("Bulk action failed:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [selectedIds, clearSelection, onClose]
  );

  /**
   * Handle color change for dashboards
   */
  const handleColorChange = useCallback(
    async (color: string) => {
      if (!onChangeDashboardsColor || selectedIds.length === 0) return;

      setIsLoading(true);
      try {
        await onChangeDashboardsColor(selectedIds, color);
        clearSelection();
        onClose();
      } catch (error) {
        console.error("Failed to change color:", error);
      } finally {
        setIsLoading(false);
        setColorPickerOpen(false);
      }
    },
    [selectedIds, onChangeDashboardsColor, clearSelection, onClose]
  );

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Get label for selected item type
  const getTypeLabel = (type: SelectableItemType | null): string => {
    switch (type) {
      case "dashboard":
        return selectionCount === 1 ? "dashboard" : "dashboards";
      case "tracker":
        return selectionCount === 1 ? "tracker" : "trackers";
      case "enrichment":
        return selectionCount === 1 ? "enrichment" : "enrichments";
      case "alert":
        return selectionCount === 1 ? "alerte" : "alertes";
      case "widget":
        return selectionCount === 1 ? "widget" : "widgets";
      case "investigation":
        return selectionCount === 1 ? "investigation" : "investigations";
      default:
        return selectionCount === 1 ? "élément" : "éléments";
    }
  };

  /**
   * Render menu items based on the selected item type
   */
  const renderMenuItems = () => {
    switch (itemType) {
      case "dashboard":
        return (
          <>
            {/* Duplicate */}
            {onDuplicateDashboards && (
              <ContextMenuItem
                onClick={wrapAction(onDuplicateDashboards)}
                disabled={isLoading}
              >
                <FaCopy /> Dupliquer ({selectionCount})
              </ContextMenuItem>
            )}

            {/* Pin */}
            {onPinDashboards && (
              <ContextMenuItem
                onClick={wrapAction((ids) => onPinDashboards(ids, true))}
                disabled={isLoading}
              >
                <FaThumbtack /> Épingler ({selectionCount})
              </ContextMenuItem>
            )}

            {/* Unpin */}
            {onUnpinDashboards && (
              <ContextMenuItem
                onClick={wrapAction(onUnpinDashboards)}
                disabled={isLoading}
              >
                <FaThumbtack className="text-yellow-400" /> Désépingler ({selectionCount})
              </ContextMenuItem>
            )}

            {/* Change color - Submenu */}
            {onChangeDashboardsColor && (
              <div className="relative">
                <ContextMenuItem
                  onClick={() => setColorPickerOpen(!colorPickerOpen)}
                  hasSubmenu
                  disabled={isLoading}
                >
                  <FaPalette /> Changer la couleur
                </ContextMenuItem>

                <AnimatePresence>
                  {colorPickerOpen && (
                    <MotionDiv
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      className="absolute left-full top-0 ml-1 bg-[var(--arc-sidebar-background)] rounded-lg shadow-xl border border-[var(--arc-sidebar-border-accent)] p-3 min-w-[200px]"
                    >
                      <ColorPicker
                        value="var(--arc-accent)"
                        onChange={handleColorChange}
                        compact
                      />
                    </MotionDiv>
                  )}
                </AnimatePresence>
              </div>
            )}

            <ContextMenuDivider />

            {/* Delete */}
            {onDeleteDashboards && (
              <ContextMenuItem
                onClick={wrapAction(onDeleteDashboards)}
                danger
                disabled={isLoading}
              >
                <FaTrash /> Supprimer ({selectionCount})
              </ContextMenuItem>
            )}
          </>
        );

      case "tracker":
        return (
          <>
            {/* Run */}
            {onRunTrackers && (
              <ContextMenuItem
                onClick={wrapAction(onRunTrackers)}
                disabled={isLoading}
              >
                <FaPlay className="text-green-500" /> Exécuter ({selectionCount})
              </ContextMenuItem>
            )}

            {/* Activate */}
            {onActivateTrackers && (
              <ContextMenuItem
                onClick={wrapAction(onActivateTrackers)}
                disabled={isLoading}
              >
                <FaToggleOn className="text-green-500" /> Activer ({selectionCount})
              </ContextMenuItem>
            )}

            {/* Deactivate */}
            {onDeactivateTrackers && (
              <ContextMenuItem
                onClick={wrapAction(onDeactivateTrackers)}
                disabled={isLoading}
              >
                <FaToggleOff /> Désactiver ({selectionCount})
              </ContextMenuItem>
            )}

            <ContextMenuDivider />

            {/* Delete */}
            {onDeleteTrackers && (
              <ContextMenuItem
                onClick={wrapAction(onDeleteTrackers)}
                danger
                disabled={isLoading}
              >
                <FaTrash /> Supprimer ({selectionCount})
              </ContextMenuItem>
            )}
          </>
        );

      case "enrichment":
        return (
          <>
            {/* Run */}
            {onRunEnrichments && (
              <ContextMenuItem
                onClick={wrapAction(onRunEnrichments)}
                disabled={isLoading}
              >
                <FaPlay className="text-green-500" /> Exécuter ({selectionCount})
              </ContextMenuItem>
            )}

            {/* Activate */}
            {onActivateEnrichments && (
              <ContextMenuItem
                onClick={wrapAction(onActivateEnrichments)}
                disabled={isLoading}
              >
                <FaToggleOn className="text-green-500" /> Activer ({selectionCount})
              </ContextMenuItem>
            )}

            {/* Deactivate */}
            {onDeactivateEnrichments && (
              <ContextMenuItem
                onClick={wrapAction(onDeactivateEnrichments)}
                disabled={isLoading}
              >
                <FaToggleOff /> Désactiver ({selectionCount})
              </ContextMenuItem>
            )}

            <ContextMenuDivider />

            {/* Delete */}
            {onDeleteEnrichments && (
              <ContextMenuItem
                onClick={wrapAction(onDeleteEnrichments)}
                danger
                disabled={isLoading}
              >
                <FaTrash /> Supprimer ({selectionCount})
              </ContextMenuItem>
            )}
          </>
        );

      case "alert":
        return (
          <>
            {/* Run */}
            {onRunAlerts && (
              <ContextMenuItem
                onClick={wrapAction(onRunAlerts)}
                disabled={isLoading}
              >
                <FaPlay className="text-green-500" /> Exécuter ({selectionCount})
              </ContextMenuItem>
            )}

            {/* Activate */}
            {onActivateAlerts && (
              <ContextMenuItem
                onClick={wrapAction(onActivateAlerts)}
                disabled={isLoading}
              >
                <FaToggleOn className="text-green-500" /> Activer ({selectionCount})
              </ContextMenuItem>
            )}

            {/* Deactivate */}
            {onDeactivateAlerts && (
              <ContextMenuItem
                onClick={wrapAction(onDeactivateAlerts)}
                disabled={isLoading}
              >
                <FaToggleOff /> Désactiver ({selectionCount})
              </ContextMenuItem>
            )}

            <ContextMenuDivider />

            {/* Delete */}
            {onDeleteAlerts && (
              <ContextMenuItem
                onClick={wrapAction(onDeleteAlerts)}
                danger
                disabled={isLoading}
              >
                <FaTrash /> Supprimer ({selectionCount})
              </ContextMenuItem>
            )}
          </>
        );

      case "widget":
        return (
          <>
            {/* Delete */}
            {onDeleteWidgets && (
              <ContextMenuItem
                onClick={wrapAction(onDeleteWidgets)}
                danger
                disabled={isLoading}
              >
                <FaTrash /> Supprimer ({selectionCount})
              </ContextMenuItem>
            )}
          </>
        );

      case "investigation":
        return (
          <>
            {/* Delete */}
            {onDeleteInvestigations && (
              <ContextMenuItem
                onClick={wrapAction(onDeleteInvestigations)}
                danger
                disabled={isLoading}
              >
                <FaTrash /> Supprimer ({selectionCount})
              </ContextMenuItem>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <MotionDiv
      ref={menuRef}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.1 }}
      className="fixed bg-[var(--arc-sidebar-background)] rounded-lg shadow-xl border border-[var(--arc-sidebar-border-accent)] py-1 min-w-[220px]"
      style={{
        top: adjustedPosition.y,
        left: adjustedPosition.x,
        zIndex: Z_INDEX.SIDEBAR_CONTEXT_MENU,
      }}
    >
      {/* Header showing selection count */}
      <div className="px-3 py-2 border-b border-[var(--arc-sidebar-border-accent)]">
        <span className="text-xs text-[var(--arc-sidebar-text-secondary)]">
          {t("n_selected", { count: selectionCount })}
        </span>
      </div>

      {/* Menu items */}
      {renderMenuItems()}
    </MotionDiv>
  );
};

MultiSelectContextMenu.displayName = "MultiSelectContextMenu";

export default MultiSelectContextMenu;
